/**
 * EIP-6963 Multi Injected Provider Discovery
 * @see https://eips.ethereum.org/EIPS/eip-6963
 *
 * Listens for wallet extension announcements and stores discovered providers.
 * Call initEip6963() once at app startup (before React renders).
 */

// Known RDNS identifiers for supported wallets
export const RDNS = {
  METAMASK: "io.metamask",
  RABBY: "io.rabby",
  COINBASE: "com.coinbase.wallet",
  PHANTOM: "app.phantom",
  TRUST: "com.trustwallet.app",
  OKX: "com.okex.wallet",
};

// Module-level state
const discoveredProviders = new Map();
let selectedProvider = null;
const listeners = new Set();

function notifyListeners() {
  const wallets = getDiscoveredWallets();
  listeners.forEach((fn) => fn(wallets));
}

/**
 * Validate that an event is a proper EIP-6963 announcement.
 */
function isAnnounceEvent(event) {
  return (
    event &&
    event.type === "eip6963:announceProvider" &&
    event.detail &&
    typeof event.detail === "object" &&
    "info" in event.detail &&
    "provider" in event.detail
  );
}

/**
 * Initialize EIP-6963 provider discovery.
 * Must be called early in the app lifecycle — before React mounts.
 * Returns a cleanup function.
 */
export function initEip6963() {
  if (typeof window === "undefined") return () => {};

  const handleAnnounce = (event) => {
    if (!isAnnounceEvent(event)) return;
    const { info, provider } = event.detail;
    // Key by rdns for stable deduplication
    discoveredProviders.set(info.rdns || info.uuid, { info, provider });
    notifyListeners();
  };

  window.addEventListener("eip6963:announceProvider", handleAnnounce);

  // Request wallets to announce themselves
  setTimeout(() => {
    window.dispatchEvent(new Event("eip6963:requestProvider"));
  }, 0);

  return () => {
    window.removeEventListener("eip6963:announceProvider", handleAnnounce);
  };
}

/**
 * Get all discovered wallet providers.
 * @returns {Array<{ info: { uuid, name, icon, rdns }, provider }>}
 */
export function getDiscoveredWallets() {
  return Array.from(discoveredProviders.values());
}

/**
 * Find a discovered wallet by its RDNS identifier.
 * @param {string} rdns
 * @returns {{ info, provider } | undefined}
 */
export function getWalletByRdns(rdns) {
  // Direct key lookup first
  const direct = discoveredProviders.get(rdns);
  if (direct) return direct;
  // Fallback: search all providers
  return Array.from(discoveredProviders.values()).find(
    (d) => d.info.rdns === rdns
  );
}

/**
 * Check if a wallet with the given RDNS is detected.
 */
export function isWalletDetected(rdns) {
  return !!getWalletByRdns(rdns);
}

/**
 * MetaMask-specific detection with legacy fallback.
 * Only trusts window.ethereum.isMetaMask when NO EIP-6963 providers exist
 * and Rabby/Coinbase/Phantom flags are absent.
 */
export function isMetaMaskAvailable() {
  const wallets = getDiscoveredWallets();
  if (wallets.length > 0) {
    return wallets.some((d) => d.info.rdns === RDNS.METAMASK);
  }
  // Legacy fallback
  if (typeof window !== "undefined" && window.ethereum) {
    return (
      !!window.ethereum.isMetaMask &&
      !window.ethereum.isRabby &&
      !window.ethereum.isCoinbaseWallet &&
      !window.ethereum.isPhantom
    );
  }
  return false;
}

export function isRabbyAvailable() {
  return getDiscoveredWallets().some((d) => d.info.rdns === RDNS.RABBY);
}

export function isCoinbaseAvailable() {
  return getDiscoveredWallets().some((d) => d.info.rdns === RDNS.COINBASE);
}

export function isTrustAvailable() {
  return getDiscoveredWallets().some((d) => d.info.rdns === RDNS.TRUST);
}

export function isOkxAvailable() {
  const wallets = getDiscoveredWallets();
  const found = wallets.some((d) => {
    const rdns = (d.info.rdns || "").toLowerCase();
    return rdns === RDNS.OKX || rdns === "com.okx.wallet" || d.provider?.isOkxWallet;
  });
  return found || !!window?.okxwallet;
}

/**
 * Set the active EIP-1193 provider chosen by the user.
 * Also assigns window.ethereum for backward compatibility with existing code.
 */
export function setSelectedProvider(provider) {
  selectedProvider = provider;
}

/**
 * Get the user-selected provider, falling back to window.ethereum.
 */
export function getSelectedProvider() {
  return selectedProvider || (typeof window !== "undefined" ? window.ethereum : null);
}

/**
 * Subscribe to wallet discovery changes. Returns unsubscribe function.
 */
export function subscribeToWalletChanges(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Get the EIP-6963 provider for a given RDNS, falling back to
 * window.ethereum for MetaMask legacy or window.okxwallet for OKX.
 */
export function getProviderForWallet(rdns) {
  const discovered = getWalletByRdns(rdns);
  if (discovered) return discovered.provider;

  // OKX fallback
  if ((rdns === RDNS.OKX || rdns === "com.okx.wallet") && window?.okxwallet) {
    return window.okxwallet;
  }

  // MetaMask legacy fallback
  if (rdns === RDNS.METAMASK && window?.ethereum?.isMetaMask && !window.ethereum.isRabby) {
    return window.ethereum;
  }

  return null;
}
